import { unwrapValueConfig } from "@smartsuite/automation-engine-api";
import type {
  InputConfig,
  InputDescription,
  IntegrationDescription,
  ValueConfig_V,
  KeyValueDescription,
} from "@smartsuite/automation-engine-api";
import { checkInputValidation } from "../../../../../../../../stores/automation/utils/inputValidation";
import { AddButton, Icon, Paragraph, Tooltip } from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import { StyledContainerWrapper } from "../../../style";
import { KeyValueItem } from "./KeyValueItem/KeyValueItem";
import { isTruthy } from "remeda";
import { translateIfPresent } from "../../../../../../../../locales/i18n";
import { GrayColors, IconColorNames, TextColorNames } from "@smartsuite/types";
import { CircleLoaderIcon } from "@smartsuite/react-ui/lib/components/loaders/CircleLoaderIcon/CircleLoaderIcon";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { updateTriggerDescription } from "../../../../../../../../services/updateDescription";
import { useEngineClient } from "../../../../../../../../services/engineClient";
import * as S from "./styles";
import { uuid } from "@smartsuite/react-ui/lib/libs/react-scrollbars-custom/util";

interface Props {
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  integrations: IntegrationDescription[];
  description?: InputDescription;
  showValidationError: boolean;
  onClickTrash?: () => void;
  hideCaptionBlock?: boolean;
}

const fillIdKeyMap = (values: ValueConfig_V[]): Map<number, string> => {
  const idToKeyValue = new Map<number, string>();
  values.forEach((value, i) => {
    if (value.value.oneofKind === "keyValue") {
      idToKeyValue.set(i, uuid());
    }
  });
  return idToKeyValue;
};

const isAddButtonAvailable = (
  currentLength: number,
  maxItems?: number,
): boolean => {
  if (maxItems === undefined) {
    return true;
  }
  return currentLength < maxItems;
};

export const KeyValueEditor = observer<Props>(
  ({
    input,
    onChange,
    trigger,
    actions,
    action,
    integrations,
    description,
    showValidationError,
    onClickTrash,
    hideCaptionBlock,
  }) => {
    const store = useRootStore();
    const engineClient = useEngineClient();
    const {
      automations: { candidate },
    } = store;

    const [onlyKeys, setOnlyKeys] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [idToKeyValue, setIdToKeyValue] = useState<Map<number, string>>(
      new Map(),
    );
    const [validationError, setValidationError] = useState("");
    const [keyValueDescription, setKeyValueDescription] = useState<
      KeyValueDescription | undefined
    >();

    useEffect(() => {
      let newInput: InputConfig | undefined;

      const keyValueDesc =
        description?.editor?.editor.oneofKind === "keyValue"
          ? description.editor.editor.keyValue
          : undefined;

      setKeyValueDescription(keyValueDesc);

      const defaultValue = keyValueDesc?.defaultValue;
      setOnlyKeys(Boolean(keyValueDesc?.onlyKeys));

      if (
        !input?.value?.values.every((v) => v.value.oneofKind === "keyValue")
      ) {
        newInput = {
          ...input,
          value:
            defaultValue === undefined
              ? { values: [] }
              : unwrapValueConfig(defaultValue),
        };
        onChange(newInput);
      }

      setIdToKeyValue(
        fillIdKeyMap(newInput?.value?.values ?? input?.value?.values ?? []),
      );
    }, []);

    useEffect(() => {
      setKeyValueDescription(
        description?.editor?.editor.oneofKind === "keyValue"
          ? description.editor.editor.keyValue
          : undefined,
      );
    }, [input]);

    useEffect(() => {
      if (showValidationError && description !== undefined) {
        setValidationError(checkInputValidation(description, input));
      } else {
        setValidationError("");
      }
    }, [input, description, showValidationError]);

    const onClickReconfigure = (): void => {
      setLoading(true);
      onChange({
        ...input,
        value: undefined,
      });

      void updateTriggerDescription(
        candidate,
        false,
        engineClient,
        store.automations,
        store.integrations,
      ).finally(() => {
        setLoading(false);
      });
    };

    const addKeyValueItem = (): void => {
      const newValues: ValueConfig_V[] = [
        ...(input?.value?.values ?? []),
        {
          value: {
            oneofKind: "keyValue",
            keyValue: {
              key: {
                values: [
                  {
                    value: {
                      oneofKind: "string",
                      string: "",
                    },
                  },
                ],
              },
              value: {
                values: [
                  {
                    value: {
                      oneofKind: "string",
                      string: onlyKeys ? " " : "",
                    },
                  },
                ],
              },
            },
          },
        },
      ];
      onChange({
        ...input,
        value: {
          values: newValues,
        },
      });
      setIdToKeyValue(fillIdKeyMap(newValues ?? []));
    };

    const onClickTrashEvent = (index: number): void => {
      (input.value?.values ?? []).splice(index, 1);
      setIdToKeyValue(fillIdKeyMap(input?.value?.values ?? []));
      onChange({
        ...input,
      });
    };

    const icon = keyValueDescription?.addButtonIcon;

    const addButtonLabel = keyValueDescription?.addButtonLabel ?? "Add";
    const showFooterValidationError =
      validationError !== "" &&
      validationError !== t("automation-form.deleted-field-validation-message");

    return (
      <StyledContainerWrapper>
        <S.CaptionWrapper>
          {description !== undefined && !hideCaptionBlock && (
            <CaptionBlock
              description={description}
              onClickTrash={onClickTrash}
            />
          )}
          {onlyKeys && (
            <Tooltip
              text={
                candidate.isActiveStatus()
                  ? translateIfPresent(
                      "caption.Reconfiguration is disabled while automation is active",
                      "Reconfiguration is disabled while automation is active",
                    )
                  : translateIfPresent(
                      "caption.Redetermine data structure",
                      "Redetermine data structure",
                    )
              }
              placements={["top", "bottom"]}
              padding={0}
              target={
                <S.ReconfigureSection>
                  {loading ? (
                    <CircleLoaderIcon />
                  ) : (
                    <Icon
                      name={"refresh"}
                      size={"M"}
                      color={
                        candidate.isActiveStatus()
                          ? IconColorNames.light
                          : IconColorNames.dark
                      }
                      onClick={() => {
                        // if it is active, we should not reconfigure
                        if (candidate.isActiveStatus()) {
                          return;
                        }
                        onClickReconfigure();
                      }}
                      transition={true}
                    />
                  )}
                </S.ReconfigureSection>
              }
            />
          )}
        </S.CaptionWrapper>
        {input.value?.values.map((item, i) => (
          <KeyValueItem
            key={idToKeyValue.get(i)}
            onChange={onChange}
            integrations={integrations}
            input={input}
            item={item}
            trigger={trigger}
            actions={actions}
            action={action}
            showValidationError={showValidationError}
            index={i}
            onClickTrash={onClickTrashEvent}
            onlyKeys={onlyKeys}
            keyValueDescription={keyValueDescription}
          />
        ))}
        {isAddButtonAvailable(
          input.value?.values?.length ?? 0,
          keyValueDescription?.maxItems,
        ) && (
          <AddButton
            onClick={addKeyValueItem}
            iconStyle={{
              name: isTruthy(icon) ? icon : "plus",
              color: GrayColors["G-7"],
              hoverColor: GrayColors["G-7"],
              marginRight: 10,
            }}
            textStyle={{
              size: "s",
              weight: "medium",
              color: TextColorNames.secondary,
              hoverColor: TextColorNames.primary,
            }}
            style={{ marginTop: 10 }}
          >
            {translateIfPresent(`caption.${addButtonLabel}`, addButtonLabel)}
          </AddButton>
        )}
        {showFooterValidationError && (
          <Paragraph color={"error"} size={"xs"}>
            {validationError}
          </Paragraph>
        )}
      </StyledContainerWrapper>
    );
  },
);
